import { Injectable, effect, inject, signal } from '@angular/core';
import { ImpersonationStore, LogService } from 'shared';

import { AuthStore } from '../features/authentication/store/auth.store';

/**
 * Single source of truth for all granular permission names used across the admin app.
 * Use these constants in route guards, menu filtering, and component-level permission checks.
 */
export const ADMIN_PERMISSIONS = {
  // Dashboard
  DASHBOARD_VIEW: 'DASHBOARD_VIEW',
  // Establishments
  ESTABLISHMENTS_VIEW_ALL: 'ESTABLISHMENTS_VIEW_ALL',
  ESTABLISHMENTS_EDIT: 'ESTABLISHMENTS_EDIT',
  // POS Terminals
  POS_TERMINALS_VIEW: 'POS_TERMINALS_VIEW',
  // Payment Links
  PAYMENT_LINKS_VIEW: 'PAYMENT_LINKS_VIEW',
  // Transactions
  TRANSACTIONS_VIEW: 'TRANSACTIONS_VIEW',
  TRANSACTIONS_CANCEL: 'TRANSACTIONS_CANCEL', // SuperAdmin-only: request/approve cancellations
  TRANSACTIONS_TRANSFER: 'TRANSACTIONS_TRANSFER', // SuperAdmin-only: bulk transfer operations
  // Reports (in-progress feature)
  REPORTS_VIEW: 'REPORTS_VIEW',
  // Settings
  SETTINGS_VIEW: 'SETTINGS_VIEW',
  EXCHANGE_RATES_VIEW: 'EXCHANGE_RATES_VIEW',
  EXCHANGE_RATES_EDIT: 'EXCHANGE_RATES_EDIT',
  CARD_FEES_VIEW: 'CARD_FEES_VIEW',
  CARD_FEES_EDIT: 'CARD_FEES_EDIT',
  PREPAID_CARD_LIMITS_EDIT: 'PREPAID_CARD_LIMITS_EDIT',
  SYSTEM_LOGS_VIEW: 'SYSTEM_LOGS_VIEW',
  // Users / Access Management
  USERS_VIEW: 'USERS_VIEW',
  USERS_EDIT: 'USERS_EDIT',
  ROLES_VIEW: 'ROLES_VIEW',
  ROLES_CREATE: 'ROLES_CREATE',
  ROLES_DELETE: 'ROLES_DELETE',
  PERMISSIONS_VIEW: 'PERMISSIONS_VIEW',
  PERMISSIONS_EDIT: 'PERMISSIONS_EDIT',
  // Compliance (in-progress features)
  AUDIT_LOGS_VIEW: 'AUDIT_LOGS_VIEW',
  SETTLEMENT_VIEW: 'SETTLEMENT_VIEW',
  RECONCILIATION_VIEW: 'RECONCILIATION_VIEW',
  RECONCILIATION_EXECUTE: 'RECONCILIATION_EXECUTE', // SuperAdmin-only: execute reconciliation actions
  // Notifications
  NOTIFICATIONS_SEND: 'NOTIFICATIONS_SEND',
  // Monitor
  MONITOR_LOGS_VIEW: 'MONITOR_LOGS_VIEW',
  MONITOR_STORAGE_VIEW: 'MONITOR_STORAGE_VIEW',
  MONITOR_DATABASE_VIEW: 'MONITOR_DATABASE_VIEW',
  MONITOR_DATABASE_QUERY: 'MONITOR_DATABASE_QUERY',
} as const;

/**
 * Permissions restricted to SuperAdmin only.
 * These are denied for Admin users regardless of backend role configuration.
 */
const SUPERADMIN_ONLY_PERMISSIONS = new Set<string>([
  ADMIN_PERMISSIONS.ROLES_CREATE,
  ADMIN_PERMISSIONS.ROLES_DELETE,
  ADMIN_PERMISSIONS.AUDIT_LOGS_VIEW,
  ADMIN_PERMISSIONS.MONITOR_STORAGE_VIEW,
  ADMIN_PERMISSIONS.MONITOR_DATABASE_QUERY,
  ADMIN_PERMISSIONS.RECONCILIATION_EXECUTE,
]);

/**
 * Utility service for checking user permissions.
 * Permissions are now loaded directly from the user profile (UserProfileDtoV2.permissions).
 * Caches permissions in localStorage to prevent access denied on page refresh.
 */
@Injectable({ providedIn: 'root' })
export class PermissionCheckerService {
  private readonly authStore = inject(AuthStore);
  private readonly impersonationStore = inject(ImpersonationStore);
  private readonly logger = inject(LogService);

  // localStorage key prefix for caching permissions
  private readonly PERMISSIONS_CACHE_KEY = 'user_permissions_';

  // Aggregated permissions for the current user
  private readonly _permissions = signal<Set<string>>(new Set());
  readonly permissions = this._permissions.asReadonly();

  constructor() {
    // CRITICAL: Load permissions from cache IMMEDIATELY on construction
    // This must happen BEFORE any route guards execute
    const currentUser = this.authStore.user();
    if (currentUser?.id) {
      const cachedPermissions = this.loadPermissionsFromCache(currentUser.id);
      if (cachedPermissions.size > 0) {
        this._permissions.set(cachedPermissions);
      }
    }

    // When the authenticated user changes, load permissions from profile
    effect(() => {
      const user = this.authStore.user();

      if (!user?.id) {
        this._permissions.set(new Set());
        return;
      }

      // Load cached permissions first (in case user changed)
      const cachedPermissions = this.loadPermissionsFromCache(user.id);
      if (cachedPermissions.size > 0) {
        this._permissions.set(cachedPermissions);
      }

      // Use permissions directly from user profile if available
      if (user.permissions && user.permissions.length > 0) {
        const newPermissions = new Set(user.permissions);
        this._permissions.set(newPermissions);
        this.savePermissionsToCache(user.id, newPermissions);
      }
    });

    // When impersonation state changes, override or restore permissions
    effect(() => {
      const isImpersonating = this.impersonationStore.isImpersonating();
      if (isImpersonating) {
        const impersonatedPerms = this.impersonationStore.impersonatedPermissions();
        this._permissions.set(new Set(impersonatedPerms));
      } else {
        // Restore real user's permissions from cache
        const user = this.authStore.user();
        if (user?.id) {
          const cached = this.loadPermissionsFromCache(user.id);
          if (cached.size > 0) {
            this._permissions.set(cached);
          } else if (user.permissions && user.permissions.length > 0) {
            this._permissions.set(new Set(user.permissions));
          }
        }
      }
    });
  }

  /**
   * Load cached permissions from localStorage for immediate availability.
   */
  private loadPermissionsFromCache(userId: string | undefined): Set<string> {
    if (!userId) {
      return new Set();
    }

    try {
      const cached = localStorage.getItem(this.PERMISSIONS_CACHE_KEY + userId);
      if (cached) {
        const permissionsArray = JSON.parse(cached) as string[];
        return new Set(permissionsArray);
      }
    } catch (error) {
      this.logger.error(
        'Failed to load cached permissions',
        'PermissionCheckerService.loadPermissionsFromCache',
        error,
      );
    }

    return new Set();
  }

  /**
   * Save permissions to localStorage for the given user.
   * Only caches non-empty permission sets to prevent caching failed states.
   */
  private savePermissionsToCache(userId: string, permissions: Set<string>): void {
    try {
      const permissionsArray = Array.from(permissions);

      // Don't cache empty permissions - might be a failed state or mid-load
      if (permissionsArray.length === 0) {
        return;
      }

      localStorage.setItem(this.PERMISSIONS_CACHE_KEY + userId, JSON.stringify(permissionsArray));
    } catch (error) {
      this.logger.error(
        'Failed to save permissions to cache',
        'PermissionCheckerService.savePermissionsToCache',
        error,
      );
    }
  }

  /**
   * Check if the user has the given permission.
   * SuperAdmin always returns true.
   * SuperAdmin-only permissions are denied for Admin users.
   */
  hasPermission(permissionName: string): boolean {
    if (this.isSuperAdmin()) {
      return true;
    }
    if (SUPERADMIN_ONLY_PERMISSIONS.has(permissionName)) {
      return false;
    }
    return this._permissions().has(permissionName);
  }

  /**
   * Check if the user has at least one of the provided permissions.
   * SuperAdmin always returns true.
   * SuperAdmin-only permissions are excluded for Admin users.
   */
  hasAnyPermission(permissionNames: string[]): boolean {
    if (this.isSuperAdmin()) {
      return true;
    }
    const current = this._permissions();
    return permissionNames.some((p) => !SUPERADMIN_ONLY_PERMISSIONS.has(p) && current.has(p));
  }

  /**
   * Check if the user has all of the provided permissions.
   * SuperAdmin always returns true.
   * SuperAdmin-only permissions are denied for Admin users.
   */
  hasAllPermissions(permissionNames: string[]): boolean {
    if (this.isSuperAdmin()) {
      return true;
    }
    const current = this._permissions();
    return permissionNames.every((p) => !SUPERADMIN_ONLY_PERMISSIONS.has(p) && current.has(p));
  }

  /**
   * Determine if the current user has the SuperAdmin role.
   * Returns false during impersonation to enforce impersonated user's role.
   */
  isSuperAdmin(): boolean {
    if (this.impersonationStore.isImpersonating()) {
      return false;
    }
    return this.authStore.isSuperAdmin();
  }

  /**
   * Clear cached permissions for the current user.
   * Useful when permissions are stale or user is stuck in access denied.
   */
  clearCache(): void {
    const user = this.authStore.user();
    if (!user?.id) {
      return;
    }

    try {
      localStorage.removeItem(this.PERMISSIONS_CACHE_KEY + user.id);
      this.logger.info('Cleared permission cache', 'PermissionCheckerService.clearCache');
    } catch (error) {
      this.logger.error(
        'Failed to clear permission cache',
        'PermissionCheckerService.clearCache',
        error,
      );
    }
  }
}
